import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

/**
 * The shared building blocks for every analytics view: one card, one stat
 * tile, one status pill — so the question bank and the business numbers read
 * as one page rather than two apps.
 */

export function Card({
  title,
  subtitle,
  icon: Icon,
  aside,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {Icon ? (
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-surface-high text-text-muted">
              <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
            </span>
          ) : null}
          <div>
            <h3 className="text-[14px] font-semibold">{title}</h3>
            {subtitle ? <p className="mt-0.5 text-[12px] text-text-muted">{subtitle}</p> : null}
          </div>
        </div>
        {aside}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  icon: Icon,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  icon?: LucideIcon;
  /** Small line under the value: a change vs the previous period, a status. */
  hint?: ReactNode;
  tone?: "accent" | "positive" | "info" | "warning";
}) {
  const toneClass = {
    accent: "bg-accent/15 text-accent",
    positive: "bg-positive/15 text-positive",
    info: "bg-info/15 text-info",
    warning: "bg-warning/15 text-warning",
  }[tone ?? "info"];
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4 lg:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12px] font-medium text-text-muted">{label}</p>
        {Icon ? (
          <span className={`grid h-7 w-7 place-items-center rounded-lg ${toneClass}`}>
            <Icon size={15} strokeWidth={2} aria-hidden="true" />
          </span>
        ) : null}
      </div>
      <p className="font-[family-name:var(--font-display)] text-[26px] font-semibold leading-none tabular-nums lg:text-[30px]">
        {value}
      </p>
      {hint ? <div className="text-[12px]">{hint}</div> : null}
    </div>
  );
}

export function Pill({
  tone,
  children,
}: {
  tone: "positive" | "warning" | "negative" | "muted";
  children: ReactNode;
}) {
  const cls = {
    positive: "bg-positive/15 text-positive",
    warning: "bg-warning/15 text-warning",
    negative: "bg-negative/15 text-negative",
    muted: "bg-surface-high text-text-muted",
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {children}
    </span>
  );
}
